// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { CustomFieldValueInput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { CFValuesForm, CustomFieldValueForm } from './CustomFieldValueForm';

interface CustomFieldValueManageProps {
    onFinish?: (CustomFieldValue: CustomFieldValueInput) => void;
    triggerElem?: ReactElement;
    defaultValues?: CustomFieldValueInput;
    onOpen?: () => void;
}

export const CustomFieldValueManage = ({
    onFinish,
    triggerElem,
    defaultValues,
    onOpen,
}: CustomFieldValueManageProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: !!defaultValues,
        fieldsTouchedInit: !!defaultValues,
    });

    const onFinishHandler = ({ order, name, type, value }: CFValuesForm): void => {
        const customFieldValue: CustomFieldValueInput = {
            order,
            name,
            type,
            value,
        };

        onFinish?.(customFieldValue);
        setFieldsTouched(false);
    };

    const tradLabelKey = !!defaultValues ? 'edit' : 'create';

    return (
        <THDrawer
            title={i(`customField.value.${tradLabelKey}.drawer.title`)}
            triggerElem={triggerElem}
            afterVisibleChange={(visible) => {
                form.resetFields();
                if (visible) {
                    onOpen?.();
                }
            }}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <CustomFieldValueForm
                    onCancel={() => {
                        setFieldsTouched(false);
                        form.resetFields();
                        toggleDrawer();
                    }}
                    onFinish={(values) => {
                        onFinishHandler(values);
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    isValid={isValid && fieldsTouched}
                    form={form}
                    defaultValues={defaultValues}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                />
            )}
        </THDrawer>
    );
};
